import { useEffect, useState } from "react"
import Layout from "../../layout/Layout"
import { useSelector, useDispatch } from "react-redux"
import { newGetBookings } from "../../../actions/admin/booking"
import { getReferrals } from "../../../actions/admin/referral"
import { Fragment } from "react"
import moment from "moment"
import { ON_GET_BOOKING } from "../../../actions/types";
import BookingsFilter from "./_components/BookingsFilter"
import BookingsTable from "./_components/BookingsTable"
import AdminCreateButton from "../_components/AdminCreateButton"
import {
  clearStoredBookingDateRange,
  getDefaultBookingDateRange,
  getInitialBookingDateRange,
  saveBookingDateRange,
} from "./constants"

const BookingList = () => {
  /**
   * * Opens on the range the admin last picked (persisted in localStorage), falling back
   * * to today through one year ahead the first time round or after clearing filters.
   */
  const [dateRange, setdateRange] = useState(getInitialBookingDateRange)
  const dispatch = useDispatch()

  const booking = useSelector((state) => state.booking)
  const { loading, bookings } = booking
  const referral = useSelector((state) => state.referral);
  const { referrals } = referral;
  const [search, setSearch] = useState("")
  const [bookingStatus, setBookingStatus] = useState([])
  const [paymentStatus, setPaymentStatus] = useState([])
  const [bookingType, setBookingType] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  })
  const [referralsOptions, setReferralsOptions] = useState([]);
  const [referralsFilter, setReferralsFilter] = useState([])

  const setFilter = (e) => {
    // Reset to page 1 when filters change

    dispatch({
      type: ON_GET_BOOKING
    })

    setCurrentPage(1)
    addFilters()
  }

  useEffect(() => {
    const formatted = referrals.map(item => ({
      value: item.code,
      label: item.name
    }));

    setReferralsOptions(formatted);

  }, [referrals])

  // Fetch referrals only once on first page load
  useEffect(() => {
    dispatch(getReferrals());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remembered as soon as the range is picked rather than on "Apply" — the request is to
  // reopen on whatever the admin last selected.
  const onDateRangeChange = (range) => {
    setdateRange(range)
    saveBookingDateRange(range)
  }

  const onResetDateRange = () => {
    setdateRange(getDefaultBookingDateRange())
    clearStoredBookingDateRange()
  }

  // Free-text search is the only filter that auto-applies (debounced).
  // All other filters apply via the "Apply" button only.
  useEffect(() => {
    const handler = setTimeout(() => {
      // Reset to page 1 when search changes
      dispatch({
        type: ON_GET_BOOKING,
      });

      setCurrentPage(1);
      addFilters();
    }, 800); // 500ms debounce (adjust as needed)

    return () => {
      clearTimeout(handler); // Cleanup on dependency change
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /**
   * * The filter will be changed / updated when any state variable is changed
   */
  useEffect(() => {
    // Reset to page 1 when filters change
    dispatch({
      type: ON_GET_BOOKING
    })

    setCurrentPage(1)
    addFilters()
  }, [sortConfig])

  const addFilters = () => {
    const queryParams = {
      search: search,
      startDate: moment(dateRange.startDate).format("YYYY-MM-DDTHH:mm:ss[Z]"),
      endDate: moment(dateRange.endDate).format("YYYY-MM-DDTHH:mm:ss[Z]"),
      isAbandoned: false,
      limit: pageSize,
      page: currentPage,
      listView: true,
    }

    // Booking status options can map to both bookingStatus and paymentStatus
    // params, and the payment status filter contributes additional paymentStatus
    // values. Merge them so the two filters compose without overwriting.
    const bookingStatusValues = new Set()
    const paymentStatusValues = new Set()

    bookingStatus.forEach((item) => {
      if (item.bookingStatus) bookingStatusValues.add(item.bookingStatus)
      ;(item.paymentStatus || []).forEach((status) => paymentStatusValues.add(status))
    })

    paymentStatus.forEach((item) => paymentStatusValues.add(item.value))

    if (bookingStatusValues.size > 0) {
      queryParams.bookingStatus = [...bookingStatusValues].join(",")
    }

    if (bookingType.length > 0) {
      queryParams.bookingType = bookingType.map((item) => item.value).join(",")
    }

    if (paymentStatusValues.size > 0) {
      queryParams.paymentStatus = [...paymentStatusValues].join(",")
    }

    if (referralsFilter.length > 0) {
      queryParams.referralCode = referralsFilter.map((item) => item.value).join(",")
    }

    // Add sorting parameters if sorting is active
    if (sortConfig.key) {
      queryParams.orderBy = sortConfig.key
      queryParams.sortBy = sortConfig.direction
    }

    dispatch(newGetBookings(queryParams))
  }

  useEffect(() => {
    if (booking && bookings?.recordsTotal) {
      setTotalPages(Math.ceil(bookings?.recordsTotal / pageSize))
    }
  }, [booking, pageSize])

  useEffect(() => {
    dispatch({
      type: ON_GET_BOOKING
    })

    addFilters()
  }, [currentPage, pageSize])

  const requestSort = (key) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })

    // Trigger API call with new sorting parameters
    setCurrentPage(1) // Reset to first page when changing sort
  }

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100">
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">
          <div className="tw:pt-[34px]">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
              <div className="tw:mb-0 tw:font-hanken-grotesk tw:text-[22px] tw:font-semibold tw:leading-[normal] tw:text-[#212529]">
                Booking List
              </div>
              {/* <AdminCreateButton to="/admin/createbookingnew" label="Make a Booking" /> */}
            </div>
            <div className="tw:mt-[34px] tw:h-px tw:w-full tw:bg-[#E6E6E6]" />
          </div>

          <BookingsFilter
            bookingStatus={bookingStatus}
            setBookingStatus={setBookingStatus}
            setdateRange={onDateRangeChange}
            resetDateRange={onResetDateRange}
            dateRange={dateRange}
            setSearch={setSearch}
            paymentStatus={paymentStatus}
            setPaymentStatus={setPaymentStatus}
            bookingType={bookingType}
            setBookingType={setBookingType}
            setFilter={setFilter}
            referralsOptions={referralsOptions}
            referralsFilter={referralsFilter}
            setReferralsFilter={setReferralsFilter}
          />

          <Fragment>
            <BookingsTable
              rows={bookings?.data || []}
              sortConfig={sortConfig}
              onSort={requestSort}
              loading={loading}
              skeletonRowCount={pageSize}
            />
            {!loading && (
              <div className="pagination-container py-3 bg-white border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    Showing {bookings?.recordsFiltered ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                    {bookings?.recordsFiltered ? Math.min(currentPage * pageSize, bookings?.recordsFiltered) : 0} of{" "}
                    {bookings?.recordsTotal || 0} entries
                  </div>
                  <div>
                    <nav aria-label="Booking pagination">
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => {
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>

                        {[...Array(Math.min(5, totalPages))].map((_, index) => {
                          // Show pages around current page
                          let pageNum
                          if (totalPages <= 5) {
                            pageNum = index + 1
                          } else if (currentPage <= 3) {
                            pageNum = index + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + index
                          } else {
                            pageNum = currentPage - 2 + index
                          }

                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                                {pageNum}
                              </button>
                            </li>
                          )
                        })}

                        <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => {
                              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                            }}
                            disabled={currentPage === totalPages || totalPages === 0}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                  <div>
                    <select
                      className="form-select form-select-sm"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value))
                        setCurrentPage(1) // Reset to first page when changing page size
                      }}
                    >
                      <option value="10">10 per page</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </Fragment>
        </div>
      </main>
    </Layout>
  )
}

export default BookingList

